import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompaniesListAPI, CompanyListItem } from '@/hooks/useCompaniesListAPI';
import { checkAdminAuthorization } from '@/utils/authHelpers';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ClaudeTaskCommand } from '@/types/claudeTask';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

// Icons
import {
  Search,
  X,
  Download,
  Grid,
  List,
  RefreshCw,
  Edit3,
  Users,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  Loader2,
  Plus
} from 'lucide-react';

// Add Company Dialog Component
interface AddCompanyDialogProps {
  onTaskCreated?: () => void;
}

const AddCompanyDialog: React.FC<AddCompanyDialogProps> = ({ onTaskCreated }) => {
  const [open, setOpen] = useState(false);
  const [pricingPageUrl, setPricingPageUrl] = useState('');
  const [companySlug, setCompanySlug] = useState('');
  const [pageSlug, setPageSlug] = useState('');
  const [navigationHint, setNavigationHint] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const resetForm = () => {
    setPricingPageUrl('');
    setCompanySlug('');
    setPageSlug('');
    setNavigationHint('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pricingPageUrl) return;

    setIsSubmitting(true);

    try {
      const params: Record<string, string> = {
        pricing_page_url: pricingPageUrl,
      };
      if (companySlug.trim()) {
        params.company_slug = companySlug.trim();
      }
      if (pageSlug.trim()) {
        params.page_slug = pageSlug.trim();
      }
      if (navigationHint.trim()) {
        params.navigation_hint = navigationHint.trim();
      }

      // Derive task slug from company_slug or URL
      const taskSlug = companySlug.trim() || new URL(pricingPageUrl).hostname.replace(/^www\./, '').split('.')[0];

      const { error } = await supabase
        .from('claude_tasks')
        .insert({
          command: ClaudeTaskCommand.RUN_ADD,
          slug: taskSlug,
          params,
          status: 'pending',
          priority: 5,
        });

      if (error) throw error;

      toast({
        title: 'Task created',
        description: `Adding company "${taskSlug}" to the queue`,
      });

      resetForm();
      setOpen(false);
      onTaskCreated?.();
    } catch (error) {
      console.error('Error creating task:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create task',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) resetForm();
    }}>
      <DialogTrigger asChild>
        <Button size="sm" className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Company
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add New Company</DialogTitle>
            <DialogDescription>
              Enter a pricing page URL to add a new company to the system.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-1.5">
              <Label htmlFor="url">Pricing Page URL *</Label>
              <Input
                id="url"
                value={pricingPageUrl}
                onChange={(e) => setPricingPageUrl(e.target.value)}
                placeholder="https://example.com/pricing"
                type="url"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="company">Company Slug</Label>
                <Input
                  id="company"
                  value={companySlug}
                  onChange={(e) => setCompanySlug(e.target.value)}
                  placeholder="e.g., slack"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="page">Page Slug</Label>
                <Input
                  id="page"
                  value={pageSlug}
                  onChange={(e) => setPageSlug(e.target.value)}
                  placeholder="e.g., enterprise"
                />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="nav-hint">Navigation Hint <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="nav-hint"
                value={navigationHint}
                onChange={(e) => setNavigationHint(e.target.value)}
                placeholder="e.g., click on the Insomnia tab"
              />
              <p className="text-xs text-muted-foreground">
                Instructions for interactive pages that require user actions before screenshotting
              </p>
            </div>

            {pricingPageUrl && (
              <p className="text-xs text-muted-foreground font-mono bg-muted/50 rounded px-2 py-1.5">
                → {companySlug || '(auto-detect)'}{pageSlug ? `.${pageSlug}` : ''}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setOpen(false);
                resetForm();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting || !pricingPageUrl}
            >
              {isSubmitting ? 'Adding...' : 'Add Company'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CompaniesV2: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
  const [isAdmin, setIsAdmin] = useState(false);
  const [localSearchInput, setLocalSearchInput] = useState('');

  const {
    companies,
    categories,
    total,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    selectedCategoryId,
    setSelectedCategoryId,
    selectedEmployees,
    setSelectedEmployees,
    hasMore,
    loadMore,
    refetch,
    uniqueEmployees,
    getCategoryName,
    filteredCompanies,
    showArchived,
    setShowArchived
  } = useCompaniesListAPI({ initialLimit: 1000 });

  // Debounce search input to prevent flickering
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(localSearchInput);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [localSearchInput, setSearchQuery]);

  // Check admin status
  useEffect(() => {
    const checkAdmin = async () => {
      const isAdminUser = await checkAdminAuthorization(navigate, (admin) => {
        setIsAdmin(admin);
      });
      setIsAdmin(isAdminUser);
    };
    checkAdmin();
  }, [navigate]);

  // Handle company click - navigate to edit page
  const handleCompanyClick = (slug: string) => {
    navigate(`/edit/${slug}`);
  };

  // Handle CSV export
  const handleExportCSV = () => {
    try {
      const headers = ['slug', 'name', 'category', 'employees', 'logo_url', 'url'];
      const rows = filteredCompanies.map(company => [
        company.slug,
        company.name || '',
        getCategoryName(company.category_id),
        company.employees || '',
        company.logo_url || '',
        company.url || ''
      ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(','));

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `companies_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Export successful',
        description: `${filteredCompanies.length} companies exported to CSV`,
        duration: 3000
      });
    } catch (err) {
      toast({
        title: 'Export failed',
        description: 'There was an error exporting the companies',
        variant: 'destructive',
        duration: 3000
      });
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    await refetch();
    toast({
      title: 'Data refreshed',
      description: 'Companies data has been refreshed',
      duration: 3000
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setLocalSearchInput('');
    setSearchQuery('');
    setSelectedCategoryId(null);
    setSelectedEmployees(null);
  };

  const hasActiveFilters = searchQuery || selectedCategoryId || selectedEmployees;

  return (
    <div className="min-h-screen from-purple-50 to-blue-50">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8 space-y-6">
          {/* Filter Section */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-sm font-medium text-gray-500">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  `Showing ${filteredCompanies.length} of ${total} companies`
                )}
              </h2>

              <div className="flex items-center gap-2">
                <AddCompanyDialog />

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  disabled={loading || filteredCompanies.length === 0}
                  className="h-8 w-8 p-0"
                  title="Export CSV"
                >
                  <Download className="h-4 w-4" />
                </Button>

                <div className="flex items-center">
                  <ToggleGroup
                    type="single"
                    value={viewMode}
                    onValueChange={(value) => value && setViewMode(value as 'cards' | 'table')}
                    className="border rounded-md"
                  >
                    <ToggleGroupItem value="table" aria-label="View as table" className="px-2 py-1 h-8">
                      <List className="h-4 w-4" />
                    </ToggleGroupItem>
                    <ToggleGroupItem value="cards" aria-label="View as cards" className="px-2 py-1 h-8">
                      <Grid className="h-4 w-4" />
                    </ToggleGroupItem>
                  </ToggleGroup>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={loading}
                    className="ml-1 px-2 h-8"
                    title="Refresh Data"
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="space-y-4">
              {/* Search */}
              <div className="relative w-full max-w-md">
                <Input
                  type="text"
                  placeholder="Search by company name..."
                  value={localSearchInput}
                  onChange={(e) => setLocalSearchInput(e.target.value)}
                  className="pl-9 h-9 w-full"
                />
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                {localSearchInput && (
                  <button
                    onClick={() => {
                      setLocalSearchInput('');
                      setSearchQuery('');
                    }}
                    className="absolute right-3 top-2.5"
                  >
                    <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>

              {/* Filter dropdowns */}
              <div className="flex flex-wrap items-start gap-2">
                {/* Category filter */}
                <Select
                  value={selectedCategoryId || 'all'}
                  onValueChange={(value) => setSelectedCategoryId(value === 'all' ? null : value)}
                >
                  <SelectTrigger className="w-[180px] h-9">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Employees filter */}
                <Select
                  value={selectedEmployees || 'all'}
                  onValueChange={(value) => setSelectedEmployees(value === 'all' ? null : value)}
                >
                  <SelectTrigger className="w-[180px] h-9">
                    <SelectValue placeholder="All Employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {uniqueEmployees.map((range) => (
                      <SelectItem key={range} value={range}>
                        {range}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-9 text-gray-500"
                  >
                    Clear filters
                  </Button>
                )}

                <div className="flex items-center gap-2 ml-auto">
                  <Switch
                    id="show-archived"
                    checked={showArchived}
                    onCheckedChange={setShowArchived}
                  />
                  <Label htmlFor="show-archived" className="text-sm text-gray-600 cursor-pointer">
                    Show archived
                  </Label>
                </div>
              </div>

              {/* Active filter badges */}
              <div className="flex flex-wrap gap-2">
                {searchQuery && (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1 px-3 py-1 bg-orange-50 hover:bg-orange-100 text-orange-700 border-0"
                  >
                    <Search className="w-3 h-3" />
                    Search: {searchQuery}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => {
                        setLocalSearchInput('');
                        setSearchQuery('');
                      }}
                    />
                  </Badge>
                )}
                {selectedCategoryId && (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1 px-3 py-1 bg-[#D3E4FD] hover:bg-[#C2D9F9] text-blue-700 border-0"
                  >
                    {getCategoryName(selectedCategoryId)}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => setSelectedCategoryId(null)}
                    />
                  </Badge>
                )}
                {selectedEmployees && (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1 px-3 py-1 bg-gray-50 hover:bg-gray-100 text-gray-700 border-0"
                  >
                    <Users className="w-3 h-3" />
                    {selectedEmployees}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => setSelectedEmployees(null)}
                    />
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Error state */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {/* Content */}
          {viewMode === 'table' ? (
            <CompanyTableViewV2
              companies={filteredCompanies}
              loading={loading}
              getCategoryName={getCategoryName}
              onCompanyClick={handleCompanyClick}
            />
          ) : (
            <CompanyCardViewV2
              companies={filteredCompanies}
              loading={loading}
              getCategoryName={getCategoryName}
              onCompanyClick={handleCompanyClick}
            />
          )}

          {/* Load more */}
          {hasMore && !loading && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={loadMore}
                className="flex items-center gap-2"
              >
                <ChevronDown className="h-4 w-4" />
                Load more ({filteredCompanies.length} of {total})
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Table View Component
interface CompanyViewProps {
  companies: CompanyListItem[];
  loading: boolean;
  getCategoryName: (categoryId: string | null) => string;
  onCompanyClick: (slug: string) => void;
}

type SortColumn = 'name' | 'category' | 'employees';
type SortDirection = 'asc' | 'desc';

// Helper to parse employee ranges for sorting
const parseEmployeeRange = (employees: string | null): number => {
  if (!employees) return 0;
  const match = employees.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

const CompanyTableViewV2: React.FC<CompanyViewProps> = ({
  companies,
  loading,
  getCategoryName,
  onCompanyClick
}) => {
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Handle column header click
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction or clear sort
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort companies
  const sortedCompanies = useMemo(() => {
    if (!sortColumn) return companies;

    return [...companies].sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'name':
          const nameA = (a.name || a.slug).toLowerCase();
          const nameB = (b.name || b.slug).toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        case 'category':
          const catA = getCategoryName(a.category_id).toLowerCase();
          const catB = getCategoryName(b.category_id).toLowerCase();
          comparison = catA.localeCompare(catB);
          break;
        case 'employees':
          comparison = parseEmployeeRange(a.employees) - parseEmployeeRange(b.employees);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [companies, sortColumn, sortDirection, getCategoryName]);

  // Sort icon component
  const SortIcon: React.FC<{ column: SortColumn }> = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-gray-400" />;
    }
    return sortDirection === 'asc'
      ? <ChevronUp className="ml-1 h-3.5 w-3.5 text-blue-600" />
      : <ChevronDown className="ml-1 h-3.5 w-3.5 text-blue-600" />;
  };

  // Sortable header component
  const SortableHeader: React.FC<{ column: SortColumn; children: React.ReactNode }> = ({ column, children }) => (
    <TableHead
      className="cursor-pointer select-none hover:bg-gray-50 transition-colors"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center">
        {children}
        <SortIcon column={column} />
      </div>
    </TableHead>
  );

  if (loading && companies.length === 0) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]"></TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Employees</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...Array(10)].map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-6 w-6 rounded" /></TableCell>
                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                <TableCell><Skeleton className="h-8 w-8 rounded" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 p-8 text-center text-gray-500">
        No companies found matching your filters
      </div>
    );
  }

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]"></TableHead>
            <SortableHeader column="name">Company</SortableHeader>
            <SortableHeader column="category">Category</SortableHeader>
            <SortableHeader column="employees">Employees</SortableHeader>
            <TableHead className="w-[80px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedCompanies.map((company) => (
            <TableRow
              key={company.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => onCompanyClick(company.slug)}
            >
              <TableCell>
                {company.logo_url && (
                  <img
                    src={company.logo_url}
                    alt={`${company.name || company.slug} logo`}
                    className="w-6 h-6 object-contain"
                  />
                )}
              </TableCell>
              <TableCell className="font-medium">
                <div className="flex flex-col">
                  <span>{company.name || company.slug}</span>
                  {company.name && company.name !== company.slug && (
                    <span className="text-xs text-gray-400">{company.slug}</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {getCategoryName(company.category_id)}
                  </Badge>
                  {company.archived && (
                    <Badge
                      variant="secondary"
                      className="bg-orange-100 text-orange-700 border-orange-200 text-xs"
                    >
                      ARCHIVED
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-gray-600">
                {company.employees || '-'}
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCompanyClick(company.slug);
                  }}
                  className="h-8 w-8 p-0 hover:bg-blue-50"
                >
                  <Edit3 className="h-4 w-4 text-blue-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// Card View Component
const CompanyCardViewV2: React.FC<CompanyViewProps> = ({
  companies,
  loading,
  getCategoryName,
  onCompanyClick
}) => {
  if (loading && companies.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(9)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start gap-3">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
            <Skeleton className="h-16 w-full mt-3" />
          </div>
        ))}
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 p-8 text-center text-gray-500">
        No companies found matching your filters
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {companies.map((company) => (
        <div
          key={company.id}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => onCompanyClick(company.slug)}
        >
          <div className="flex items-start gap-3">
            {company.logo_url ? (
              <img
                src={company.logo_url}
                alt={`${company.name || company.slug} logo`}
                className="h-12 w-12 object-contain rounded-lg bg-gray-50 p-1"
              />
            ) : (
              <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-lg font-medium">
                {(company.name || company.slug).charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate">
                {company.name || company.slug}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs">
                  {getCategoryName(company.category_id)}
                </Badge>
                {company.archived && (
                  <Badge
                    variant="secondary"
                    className="bg-orange-100 text-orange-700 border-orange-200 text-xs"
                  >
                    ARCHIVED
                  </Badge>
                )}
                {company.employees && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {company.employees}
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onCompanyClick(company.slug);
              }}
              className="h-8 w-8 p-0 hover:bg-blue-50"
            >
              <Edit3 className="h-4 w-4 text-blue-600" />
            </Button>
          </div>
          {company.description && (
            <p className="mt-3 text-sm text-gray-600 line-clamp-2">
              {company.description}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default CompaniesV2;
